import {
  ACTION_STATUS_LABELS,
  APPROVAL_COMMENT_MAX,
  DECISION_APPROVAL_LABELS,
  DECISION_APPROVERS_MAX,
  DECISION_VOTE_LABELS,
  agendaOptionsSchema,
  decisionSchema,
  decisionVoteSchema,
  formatDateTime,
  type Decision,
  type DecisionApprovalStatusName,
  type TrafficLight,
} from '@fluxus/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

const APPROVAL_LIGHT: Record<DecisionApprovalStatusName, TrafficLight> = {
  NONE: 'gray',
  PENDING: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
};

/** A ação da decisão ainda está aberta (rejeitar pede cancelar ou manter). */
export const actionOpen = (d: Decision) =>
  d.action !== null && !['DONE', 'CANCELLED'].includes(d.action.status);

/** Alguém já votou: o texto, o responsável e o prazo não mudam mais, e a decisão não sai (DEC-053). */
export const approvalStarted = (d: Decision) => d.approval.approvers.some((a) => a.vote !== 'PENDING');

export function ApprovalBadge({ status }: { status: DecisionApprovalStatusName }) {
  return <StatusBadge status={APPROVAL_LIGHT[status]} label={DECISION_APPROVAL_LABELS[status]} />;
}

/** Estado da aprovação e os votos (quem, quando, comentário e o que foi feito com a ação). */
export function ApprovalSummary({ d }: { d: Decision }) {
  if (d.approval.status === 'NONE') return null;
  return (
    <div className="flex flex-col gap-1 text-xs">
      <p className="flex flex-wrap items-center gap-2 text-muted-foreground">
        <ApprovalBadge status={d.approval.status} />
        {d.approval.decidedAt ? <span>em {formatDateTime(d.approval.decidedAt)}</span> : null}
      </p>
      <ul aria-label={`Votos da decisão ${d.text}`} className="flex flex-col gap-0.5">
        {d.approval.approvers.map((a) => (
          <li key={a.id} className="break-words">
            <span className="font-semibold">{a.name}</span>: {DECISION_VOTE_LABELS[a.vote].toLowerCase()}
            {a.decidedAt ? ` em ${formatDateTime(a.decidedAt)}` : ''}
            {a.comment ? ` — "${a.comment}"` : ''}
            {a.actionCancelled === true
              ? ' · ação cancelada'
              : a.actionCancelled === false
                ? ' · ação mantida'
                : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Escolha dos aprovadores (até 10): quem já votou continua marcado e não sai. */
export function ApproverPicker({
  approvers,
  value,
  onChange,
  locked = [],
  error,
  legend = 'Aprovadores (opcional)',
}: {
  approvers: { id: string; name: string }[];
  value: string[];
  onChange: (ids: string[]) => void;
  locked?: { id: string; name: string }[];
  error?: string;
  legend?: string;
}) {
  const errorId = useId();
  // Quem votou e não está mais entre os que podem aprovar continua na lista (marcado e travado).
  const list = [...locked.filter((l) => !approvers.some((a) => a.id === l.id)), ...approvers];
  const lockedIds = new Set(locked.map((l) => l.id));
  const full = value.length >= DECISION_APPROVERS_MAX;
  return (
    <fieldset
      className="flex flex-col gap-1"
      aria-describedby={error ? errorId : undefined}
      aria-invalid={error ? true : undefined}
    >
      <legend className="text-sm font-medium">{legend}</legend>
      {list.length ? (
        <>
          <p className="text-xs text-muted-foreground">
            Com aprovadores, a decisão só vale depois que todos aprovam; se alguém rejeitar, ela é rejeitada.
            Até {DECISION_APPROVERS_MAX} pessoas.
          </p>
          <div className="grid gap-x-3 sm:grid-cols-2">
            {list.map((a) => {
              const checked = value.includes(a.id);
              return (
                <label key={a.id} className="inline-flex min-h-11 items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-(--brand)"
                    checked={checked}
                    disabled={lockedIds.has(a.id) || (!checked && full)}
                    onChange={(e) =>
                      onChange(e.target.checked ? [...value, a.id] : value.filter((id) => id !== a.id))
                    }
                  />
                  <span className="break-words">
                    {a.name}
                    {lockedIds.has(a.id) ? ' (já votou)' : ''}
                  </span>
                </label>
              );
            })}
          </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          Ninguém na empresa tem "aprovar" em Reuniões e Decisões: a decisão é registrada sem aprovação.
        </p>
      )}
      {error ? (
        <p id={errorId} className="text-xs text-status-red-fg">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

/**
 * Voto do aprovador (DEC-053): aprovar (comentário opcional) ou rejeitar (comentário obrigatório); rejeitar com a
 * ação da decisão aberta pede cancelar ou manter a ação.
 */
export function VoteDialog({
  organizationId,
  projectId,
  decision,
  approve,
  onClose,
  onVoted,
}: {
  organizationId: string;
  projectId: string;
  decision: Decision | null;
  approve: boolean;
  onClose: () => void;
  onVoted: (d: Decision) => Promise<unknown>;
}) {
  return (
    <Dialog
      open={decision !== null}
      onOpenChange={(open) => !open && onClose()}
      title={approve ? 'Aprovar a decisão' : 'Rejeitar a decisão'}
      description={decision?.text}
    >
      {decision ? (
        <VoteForm
          key={`${decision.id}-${approve}`}
          organizationId={organizationId}
          projectId={projectId}
          decision={decision}
          approve={approve}
          onCancel={onClose}
          onVoted={onVoted}
        />
      ) : null}
    </Dialog>
  );
}

function VoteForm({
  organizationId,
  projectId,
  decision,
  approve,
  onCancel,
  onVoted,
}: {
  organizationId: string;
  projectId: string;
  decision: Decision;
  approve: boolean;
  onCancel: () => void;
  onVoted: (d: Decision) => Promise<unknown>;
}) {
  const { client } = useAuth();
  const ids = { comment: useId(), action: useId() };
  const [comment, setComment] = useState('');
  const [cancelAction, setCancelAction] = useState<boolean | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const asksAction = !approve && actionOpen(decision);
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: (body: Record<string, unknown>) =>
      client.request(
        `/organizations/${organizationId}/projects/${projectId}/meeting-decisions/${decision.id}/approval`,
        { method: 'POST', body, schema: decisionSchema },
      ),
    onSuccess: async (d) => {
      setErrors({});
      await onVoted(d);
    },
    onError: (err) => {
      const fields = apiFieldErrors(err);
      setErrors(hasFieldErrors(fields) ? fields : { form: errorMessage(err) });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = decisionVoteSchema.safeParse({
      approve,
      comment,
      cancelAction: asksAction ? cancelAction : null,
    });
    // Todos os erros de uma vez: o motivo e a escolha sobre a ação.
    const found: FieldErrors = parsed.success ? {} : zodFieldErrors(parsed.error);
    if (asksAction && cancelAction === null) {
      found.cancelAction = 'Escolha se a ação da decisão é cancelada ou mantida.';
    }
    if (!parsed.success || hasFieldErrors(found)) return setErrors(found);
    setErrors({});
    save.mutate(parsed.data);
  };
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3">
      {errors.form ? <FormAlert tone="error">{errors.form}</FormAlert> : null}
      <FormField
        id={ids.comment}
        label={approve ? 'Comentário (opcional)' : 'Motivo da rejeição'}
        error={errors.comment}
      >
        <textarea
          {...fieldA11y(ids.comment, errors.comment)}
          value={comment}
          rows={3}
          maxLength={APPROVAL_COMMENT_MAX}
          onChange={(e) => setComment(e.target.value)}
          className={textareaClass}
          placeholder={
            approve ? 'Observação para quem registrou a decisão' : 'Por que a decisão não deve valer'
          }
        />
      </FormField>
      {asksAction && decision.action ? (
        <fieldset
          className="flex flex-col gap-1"
          aria-describedby={errors.cancelAction ? ids.action : undefined}
          aria-invalid={errors.cancelAction ? true : undefined}
        >
          <legend className="text-sm font-medium">
            A ação "{decision.action.title}" (
            {ACTION_STATUS_LABELS[decision.action.status as keyof typeof ACTION_STATUS_LABELS] ??
              decision.action.status}
            )
          </legend>
          <label className="inline-flex min-h-11 items-center gap-2">
            <input
              type="radio"
              name={ids.action}
              className="size-4 accent-(--brand)"
              checked={cancelAction === true}
              onChange={() => setCancelAction(true)}
            />
            Cancelar a ação (o motivo vai como justificativa)
          </label>
          <label className="inline-flex min-h-11 items-center gap-2">
            <input
              type="radio"
              name={ids.action}
              className="size-4 accent-(--brand)"
              checked={cancelAction === false}
              onChange={() => setCancelAction(false)}
            />
            Manter a ação
          </label>
          {errors.cancelAction ? (
            <p id={ids.action} className="text-xs text-status-red-fg">
              {errors.cancelAction}
            </p>
          ) : null}
        </fieldset>
      ) : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" disabled={save.isPending} onClick={onCancel}>
          Voltar
        </Button>
        <Button type="submit" variant={approve ? 'primary' : 'danger'} loading={save.isPending}>
          {approve ? 'Aprovar' : 'Rejeitar'}
        </Button>
      </div>
    </form>
  );
}

/**
 * Trocar os aprovadores pendentes (DEC-053): enquanto a aprovação não termina — também com a reunião encerrada —, quem
 * conduz a análise troca quem ainda não votou (ex.: saiu da empresa). Quem já votou continua.
 */
export function SwapApproversDialog({
  organizationId,
  projectId,
  decision,
  onClose,
  onSaved,
}: {
  organizationId: string;
  projectId: string;
  decision: Decision | null;
  onClose: () => void;
  onSaved: (d: Decision) => Promise<unknown>;
}) {
  return (
    <Dialog
      open={decision !== null}
      onOpenChange={(open) => !open && onClose()}
      title="Trocar aprovadores"
      description={decision?.text}
      className="w-[min(36rem,calc(100vw-2rem))]"
    >
      {decision ? (
        <SwapForm
          key={decision.id}
          organizationId={organizationId}
          projectId={projectId}
          decision={decision}
          onCancel={onClose}
          onSaved={onSaved}
        />
      ) : null}
    </Dialog>
  );
}

function SwapForm({
  organizationId,
  projectId,
  decision,
  onCancel,
  onSaved,
}: {
  organizationId: string;
  projectId: string;
  decision: Decision;
  onCancel: () => void;
  onSaved: (d: Decision) => Promise<unknown>;
}) {
  const { client } = useAuth();
  const base = `/organizations/${organizationId}/projects/${projectId}`;
  const options = useQuery({
    queryKey: ['meetings', projectId, 'agenda', decision.meetingId, 'options'],
    queryFn: ({ signal }) =>
      client.request(`${base}/meetings/${decision.meetingId}/agenda/options`, {
        schema: agendaOptionsSchema,
        signal,
      }),
  });
  const voted = decision.approval.approvers.filter((a) => a.vote !== 'PENDING');
  const [value, setValue] = useState(decision.approval.approvers.map((a) => a.id));
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: (approverIds: string[]) =>
      client.request(`${base}/meeting-decisions/${decision.id}/approvers`, {
        method: 'PUT',
        body: { approverIds },
        schema: decisionSchema,
      }),
    onSuccess: async (d) => {
      setErrors({});
      await onSaved(d);
    },
    onError: (err) => {
      const fields = apiFieldErrors(err);
      setErrors(hasFieldErrors(fields) ? fields : { form: errorMessage(err) });
    },
  });
  if (options.isPending) return <LoadingState rows={2} label="Carregando quem pode aprovar" />;
  if (options.isError) {
    return (
      <ErrorState
        error={options.error}
        onRetry={() => void options.refetch()}
        retrying={options.isFetching}
      />
    );
  }
  // Pendentes que não podem mais aprovar (ex.: saíram da empresa) aparecem para serem desmarcados.
  const pendingGone = decision.approval.approvers.filter(
    (a) => a.vote === 'PENDING' && !options.data.approvers.some((p) => p.id === a.id),
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(value);
      }}
      noValidate
      className="flex flex-col gap-3"
    >
      {errors.form ? <FormAlert tone="error">{errors.form}</FormAlert> : null}
      {pendingGone.length ? (
        <FormAlert tone="locked">
          {pendingGone.map((a) => a.name).join(', ')}{' '}
          {pendingGone.length === 1 ? 'não pode mais aprovar' : 'não podem mais aprovar'}: desmarque e, se
          precisar, escolha outra pessoa.
        </FormAlert>
      ) : null}
      <ApproverPicker
        legend="Aprovadores"
        approvers={[...pendingGone, ...options.data.approvers]}
        locked={voted}
        value={value}
        onChange={setValue}
        error={errors.approverIds}
      />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" disabled={save.isPending} onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={save.isPending}>
          Salvar aprovadores
        </Button>
      </div>
    </form>
  );
}
